import { useEffect, useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { cn } from "@/lib/utils/cn";
import { DEFAULT_LOGO_SRC, DEFAULT_PRODUCT_NAME, useBranding } from "@/lib/branding";
import { useAuthStore, useActiveTenant } from "@/stores/auth-store";
import { exitWorkspace } from "@/lib/workspace";
import { Icon } from "./Icon";
import { Tooltip } from "@/components/ui/Tooltip";
import { useImageFallback } from "@/lib/utils/useImageFallback";
import { useNavVisibility } from "./useNavVisibility";
import { useActiveModule } from "./useActiveModule";
import {
  MODULE_ICON_COLOR_CLASSES,
  NAV_SECTIONS,
  WORKSPACE_NAV_ITEMS,
  getVisibleModuleTiles,
  isNavItemVisible,
} from "./nav-config";

/** Whether the desktop sidebar starts collapsed (icons only, labels
 * hidden) - read once at mount from localStorage so a returning user's last
 * choice survives a full reload, not just client-side navigation. */
const COLLAPSED_STORAGE_KEY = "hrms.sidebar.collapsed";


function readStoredCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * The stable global sidebar: every module this caller can reach, always
 * visible, never swapped out for a module-specific list. Moving between
 * modules changes the tab strip inside the page (ModuleHeader), not this
 * column - so there is no "drill in, then find your way back" step.
 *
 * Collapsing hides the labels and leaves an icon rail; it does not change
 * which modules are listed.
 */
export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const user = useAuthStore((s) => s.user);
  const tenant = useActiveTenant();
  const impersonatedTenant = useAuthStore((s) => s.impersonatedTenant);
  // A Super Admin who has opened a workspace gets a completely separate,
  // narrower nav (WORKSPACE_NAV_ITEMS) instead of the module list below -
  // not just the tenant HR items hidden, the whole Super Admin Console too.
  // This is what actually keeps platform modules from "appearing inside
  // tenant workspaces" - every `superAdminOnly` item would otherwise render
  // regardless of workspace state. Unchanged by the tenant-side nav work.
  const inWorkspaceMode = Boolean(user?.is_super_admin) && Boolean(impersonatedTenant);
  const ctx = useNavVisibility();
  const activeModule = useActiveModule();

  const [collapsed, setCollapsed] = useState(readStoredCollapsed);

  // The workspace's own uploaded logo (Company Settings -> logo), falling
  // back to the bundled product mark. `tenant.logo_url` is origin-relative
  // ("/static/logos/...", see STORAGE_PUBLIC_URL) and is served by the
  // backend's public image route, so it works on any tenant domain without
  // per-tenant configuration. useImageFallback is keyed on the URL, so a
  // logo that fails once does not latch the fallback in place for the rest
  // of the session once a working URL arrives.
  //
  // A Super-Admin-configured white-label logo/name for this domain (see
  // lib/branding) takes precedence over both.
  const branding = useBranding();
  const logoUrl = branding.customLogoSrc || (tenant?.logo_url?.trim() ? tenant.logo_url : null);
  const brandName = branding.customCompanyName || tenant?.name || DEFAULT_PRODUCT_NAME;
  const { showImage: showTenantLogo, onError: onLogoError } = useImageFallback(logoUrl);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      // Storage can be unavailable (private browsing, quota) - the toggle
      // still works for the current session, it just won't persist.
    }
  }, [collapsed]);

  const tiles = useMemo(() => getVisibleModuleTiles(ctx), [ctx]);

  // The Profile Settings footer is driven by nav-config's own convention
  // (last section = always the bottom-most item) rather than a second,
  // hand-duplicated button.
  const profileSettingsItem = NAV_SECTIONS[NAV_SECTIONS.length - 1]!.items[0]!;
  // Dashboard (nav-config's own first, cross-cutting, moduleKey-less
  // section) is pinned above the module list rather than being one of them -
  // it isn't a module with its own destinations, just the landing page.
  const dashboardItem = NAV_SECTIONS[0]!.items[0]!;
  const showDashboard = isNavItemVisible(dashboardItem, ctx);

  const showLabels = !collapsed;

  return (
    <>
      {open && <div className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden" onClick={onClose} aria-hidden />}

      <aside
        className={cn(
          // `h-screen` is set explicitly (not left to the `inset-y-0`
          // top+bottom trick alone) because that trick only sizes a
          // positioned element - once `lg:static` below turns `position`
          // static, `inset-y-0` stops affecting layout entirely and the
          // sidebar would otherwise shrink to its content's height.
          "fixed inset-y-0 left-0 z-40 flex h-screen w-72 flex-col border-r border-slate-200 bg-white transition-all duration-200 ease-out",
          open ? "translate-x-0 shadow-xl" : "-translate-x-full",
          "lg:static lg:translate-x-0 lg:shadow-none",
          showLabels || inWorkspaceMode ? "lg:w-64" : "lg:w-[68px]",
        )}
      >
        <div className={cn("flex h-16 shrink-0 items-center border-b border-slate-200 px-3", showLabels ? "justify-between" : "justify-center")}>
          <NavLink
            to="/modules"
            onClick={onClose}
            title="All modules"
            className="flex min-w-0 items-center gap-2.5 transition-opacity duration-200 ease-out hover:opacity-90"
          >
            {showTenantLogo && logoUrl ? (
              <img
                src={logoUrl}
                alt={`${branding.customCompanyName || tenant?.name || "Workspace"} logo`}
                className="h-9 w-9 shrink-0 rounded-lg object-contain"
                onError={onLogoError}
              />
            ) : (
              <img src={DEFAULT_LOGO_SRC} alt={`${DEFAULT_PRODUCT_NAME} logo`} className="h-9 w-9 shrink-0 rounded-lg object-contain" />
            )}
            {showLabels && (
              <span className="truncate font-display text-sm font-semibold text-slate-800">{brandName}</span>
            )}
          </NavLink>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 transition-colors duration-200 ease-out hover:bg-primary-50 hover:text-primary-700 lg:hidden"
            aria-label="Close menu"
          >
            <Icon name="x" className="h-5 w-5" />
          </button>
        </div>

        {inWorkspaceMode ? (
          <div className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4">
            <p className="mb-1.5 px-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Tenant Workspace</p>
            <div className="flex flex-col gap-0.5">
              {WORKSPACE_NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      "group flex items-center gap-2.5 rounded-r-lg border-l-2 px-2.5 py-2 text-sm font-medium transition-all duration-200 ease-out",
                      isActive
                        ? "border-primary-700 bg-primary-50 font-semibold text-primary-900 ring-1 ring-inset ring-primary-100"
                        : "border-transparent text-slate-600 hover:border-primary-200 hover:bg-primary-50/70 hover:text-primary-800",
                    )
                  }
                >
                  <Icon name={item.icon} className="h-4 w-4 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ) : (
          <nav aria-label="Modules" className={cn("flex-1 overflow-y-auto scrollbar-thin py-3", showLabels ? "px-3" : "px-2")}>
            {showDashboard && (
              <div className={cn("mb-2 border-b border-slate-100 pb-2", !showLabels && "flex flex-col items-center")}>
                <SidebarLink
                  to={dashboardItem.to}
                  label={dashboardItem.label}
                  icon={dashboardItem.icon}
                  showLabel={showLabels}
                  exact
                  onNavigate={onClose}
                />
              </div>
            )}
            <div className={cn("flex flex-col gap-0.5", !showLabels && "items-center")}>
              {tiles.map((tile) => (
                <SidebarLink
                  key={tile.key}
                  to={tile.to}
                  label={tile.label}
                  icon={tile.icon}
                  iconColorClass={MODULE_ICON_COLOR_CLASSES[tile.color]}
                  showLabel={showLabels}
                  // "Active" here means "this module owns the current
                  // route", which is a different question from whether
                  // tile.to (the module's first visible item) equals the
                  // current URL - NavLink's own route-match state would
                  // disagree with, and override, what we want here.
                  active={activeModule?.sidebarKey === tile.key}
                  onNavigate={onClose}
                />
              ))}
            </div>
          </nav>
        )}

        <div className={cn("mt-auto flex shrink-0 items-center gap-2 border-t border-slate-200 p-3", showLabels ? "justify-between" : "flex-col justify-center")}>
          <SidebarLink
            to={profileSettingsItem.to}
            label="Profile Settings"
            icon="settings"
            showLabel={showLabels}
            exact
            onNavigate={() => {
              if (profileSettingsItem.superAdminOnly) exitWorkspace();
              onClose();
            }}
          />
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-pressed={collapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors duration-200 ease-out hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700 lg:flex"
          >
            <Icon name={collapsed ? "chevron-right" : "chevron-left"} className="h-3.5 w-3.5" />
          </button>
        </div>
      </aside>
    </>
  );
}

function SidebarLink({
  to,
  label,
  icon,
  iconColorClass,
  showLabel,
  active,
  exact,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: string;
  iconColorClass?: string;
  showLabel: boolean;
  active?: boolean;
  exact?: boolean;
  onNavigate: () => void;
}) {
  function body(on: boolean) {
    return (
      <>
        <Icon name={icon} className={cn("h-5 w-5 shrink-0", !on && iconColorClass)} />
        {showLabel && <span className="truncate">{label}</span>}
      </>
    );
  }

  function classes(on: boolean) {
    return cn(
      "group flex items-center rounded-lg text-sm font-medium transition-all duration-200 ease-out",
      showLabel ? "w-full gap-2.5 px-2.5 py-2" : "h-10 w-10 justify-center",
      on
        ? "bg-primary-50 font-semibold text-primary-900 ring-1 ring-inset ring-primary-100"
        : "text-slate-600 hover:bg-primary-50/70 hover:text-primary-800",
    );
  }

  // A plain Link, not NavLink, whenever `active` is supplied: "active" for a
  // module means "this module owns the current route", which is a different
  // question from whether `to` (the module's first visible destination)
  // equals the current URL. NavLink computes its own aria-current from the
  // latter and would overwrite the former with undefined.
  const link =
    active !== undefined ? (
      <Link to={to} onClick={onNavigate} aria-label={label} aria-current={active ? "page" : undefined} className={classes(active)}>
        {body(active)}
      </Link>
    ) : (
      <NavLink to={to} end={exact} onClick={onNavigate} aria-label={label} className={({ isActive }) => classes(isActive)}>
        {({ isActive }) => body(isActive)}
      </NavLink>
    );

  // Collapsed to an icon rail, the label is the only thing identifying the
  // module - surface it on hover rather than leaving unlabelled icons.
  return showLabel ? link : <Tooltip label={label} side="bottom">{link}</Tooltip>;
}
